import { t } from '../ui/lang';
import { esc } from '../ui/modal';
import { renderLineChart, renderLineTable } from './line-chart';
/** Known statuses in their fixed legend order, with their colour and line style. */
const STATUSES = [
    { key: 'sent', color: 'var(--viz-sent)' },
    { key: 'success', color: 'var(--viz-success)' },
    { key: 'cancel', color: 'var(--viz-cancel)' },
    { key: 'decline', color: 'var(--viz-decline)' },
    { key: 'in_work', color: 'var(--viz-neutral)', dashed: true },
];
const OTHER_COLOR = 'var(--viz-neutral)';
const two = (n) => String(n).padStart(2, '0');
const dayKey = (d) => `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
/** Every bucket of the range in local time, keyed the way the server formats them. The grid
 *  starts on the bucket holding `from`, since the range itself may start mid-hour or mid-day. */
function buckets(from, to, bucket) {
    const out = [];
    const d = new Date(from);
    d.setMinutes(0, 0, 0);
    if (bucket === 'day')
        d.setHours(0);
    while (d < to) {
        const day = d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' });
        if (bucket === 'day') {
            out.push({ key: dayKey(d), label: day, title: d.toLocaleDateString() });
            d.setDate(d.getDate() + 1);
        }
        else {
            const hh = `${two(d.getHours())}:00`;
            out.push({ key: `${dayKey(d)}T${two(d.getHours())}`, label: d.getHours() === 0 ? day : hh, title: `${d.toLocaleDateString()} ${hh}` });
            d.setHours(d.getHours() + 1);
        }
    }
    return out;
}
/** Mount the chart card into `host`; returns a loader to call whenever the filters change. */
export function mountOpsStatusChart(host) {
    host.innerHTML = `
        <section class="viz chart-card">
            <header class="chart-card__head">
                <div>
                    <h3 class="chart-card__title">${esc(t('charts.ops_status_title'))}</h3>
                    <div class="settings-hint chart-card__sub"></div>
                </div>
                <div class="chart-card__views" role="tablist">
                    <button type="button" class="tbl-btn chart-view is-on" data-view="chart">${esc(t('charts.view_chart'))}</button>
                    <button type="button" class="tbl-btn chart-view" data-view="table">${esc(t('charts.view_table'))}</button>
                </div>
            </header>
            <div class="chart-card__body"><p class="settings-hint">${esc(t('common.loading'))}</p></div>
        </section>
    `;
    const body = host.querySelector('.chart-card__body');
    const sub = host.querySelector('.chart-card__sub');
    let data = null;
    let view = 'chart';
    const hidden = new Set();
    let seq = 0;
    const draw = () => {
        if (!data)
            return;
        if (!data.xLabels.length || data.series.every((s) => s.values.every((v) => v === 0))) {
            body.innerHTML = `<p class="settings-hint">${esc(t('charts.empty'))}</p>`;
            return;
        }
        if (view === 'table')
            renderLineTable(body, data, t('charts.col_period'));
        else
            renderLineChart(body, data, hidden, (key) => { if (!hidden.delete(key))
                hidden.add(key); draw(); });
    };
    host.querySelectorAll('.chart-view').forEach((b) => {
        b.addEventListener('click', () => {
            view = b.dataset['view'] === 'table' ? 'table' : 'chart';
            host.querySelectorAll('.chart-view').forEach((x) => x.classList.toggle('is-on', x === b));
            draw();
        });
    });
    // Redraw at the new width; the router swapping #content disconnects the host.
    let lastW = 0;
    const ro = new ResizeObserver(() => {
        if (!host.isConnected) {
            ro.disconnect();
            return;
        }
        const w = body.clientWidth;
        if (view === 'chart' && Math.abs(w - lastW) > 4) {
            lastW = w;
            draw();
        }
    });
    ro.observe(body);
    return (f) => {
        const mine = ++seq;
        const from = new Date(f.from);
        const to = new Date(f.to);
        if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to <= from)
            return;
        body.innerHTML = `<p class="settings-hint">${esc(t('common.loading'))}</p>`;
        const p = new URLSearchParams({
            from: from.toISOString(), to: to.toISOString(),
            tzOffset: String(-new Date().getTimezoneOffset()),
        });
        if (f.farmId)
            p.set('farmId', f.farmId);
        if (f.teamId)
            p.set('teamId', f.teamId);
        if (f.type)
            p.set('type', f.type);
        if (f.app)
            p.set('app', f.app);
        if (f.bucket)
            p.set('bucket', f.bucket);
        void fetch(`/api/charts/operations-status?${p.toString()}`).then(async (r) => {
            if (mine !== seq)
                return;
            if (!r.ok) {
                const code = r.status === 400 ? (await r.json().catch(() => ({}))).code : undefined;
                if (mine !== seq)
                    return;
                const key = r.status !== 400 ? 'common.failed_to_load'
                    : code === 'hourly_range' ? 'charts.hourly_too_long' : 'charts.range_too_long';
                body.innerHTML = `<p class="settings-hint settings-hint--err">${esc(t(key))}</p>`;
                return;
            }
            const res = await r.json();
            if (mine !== seq)
                return;
            sub.textContent = t(res.bucket === 'hour' ? 'charts.ops_status_hint_hour' : 'charts.ops_status_hint_day');
            const bs = buckets(from, to, res.bucket);
            const index = new Map(bs.map((b, i) => [b.key, i]));
            const counts = new Map();
            for (const row of res.rows) {
                const i = index.get(row.t);
                if (i == null)
                    continue;
                const arr = counts.get(row.status) ?? new Array(bs.length).fill(0);
                arr[i] = (arr[i] ?? 0) + row.n;
                counts.set(row.status, arr);
            }
            const known = new Set(STATUSES.map((s) => s.key));
            const series = [
                ...STATUSES.filter((s) => counts.has(s.key)).map((s) => ({
                    key: s.key, label: t(`charts.status_${s.key}`), color: s.color, dashed: s.dashed, values: counts.get(s.key),
                })),
                // A status the panel does not know yet is still shown, under its raw name.
                ...[...counts.keys()].filter((k) => !known.has(k)).sort().map((k) => ({
                    key: k, label: k || '—', color: OTHER_COLOR, dashed: true, values: counts.get(k),
                })),
            ];
            data = { xLabels: bs.map((b) => b.label), xTitles: bs.map((b) => b.title), series };
            draw();
        }).catch(() => {
            if (mine === seq)
                body.innerHTML = `<p class="settings-hint settings-hint--err">${esc(t('common.failed_to_load'))}</p>`;
        });
    };
}
